import React, {useState, useEffect} from 'react';
import {injectIntl} from 'react-intl';
import uuid from 'uuid';
import Moment from 'moment';
import PropTypes from 'prop-types';

import CreateTimeRegistration from '../../mutations/create_time_registration_mutation.modern';

import Util from '../../forecast-app/shared/util/util';
import {BUTTON_STYLE, BUTTON_COLOR, DATE_PICKER_STYLE} from '../../constants';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import DatePicker from '../../forecast-app/shared/components/date-picker/date_picker_v3';
import TimeRegWSuggestionsInput from '../../forecast-app/shared/components/inputs/time-registration-input/time_reg_w_sugestions_input';
import TimeRegContainer from '../../styles/components/time_reg_styled';
import Dropdown from '../../forecast-app/shared/components/dropdowns/dropdown';
import DropdownV2 from '../../forecast-app/shared/components/dropdowns/dropdown';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {dispatch, EVENT_ID} from '../../containers/event_manager';

export const TimeReg = ({viewer, task, intl: {formatMessage}, onDone, isTaskModal, focusOnMount, harvestTaskOptions, unit4ActivityOptions, lockedDate}) => {
	const today = Moment();
	const todayOrEarliestSelectableDate = lockedDate && lockedDate.isSameOrAfter(today, 'day') ? lockedDate.clone().add(1, 'd') : today;

	const [id] = useState(uuid.v4());
	const [inputValue, setInputValue] = useState('');
	const [selectedDate, setSelectedDate] = useState(todayOrEarliestSelectableDate);
	const [notes, setNotes] = useState('');
	const [selectedHarvestTask, setSelectedHarvestTask] = useState(null);
	const [selectedUnit4Activity, setSelectedUnit4Activity] = useState(null);
	const [unit4Loading, setUnit4Loading] = useState(false);
	const [unit4Activities, setUnit4Activities] = useState([]);

	let inputRef; // ref for pointing to hours input

	// Focus input on mount if prop is passed
	useEffect(() => {
		if (focusOnMount && inputRef) {
			inputRef.focus();
		}
	}, []);

	useEffect(() => {
		if (lockedDate && lockedDate.isSameOrAfter(selectedDate, 'day')) {
			setSelectedDate(lockedDate.clone().add(1, 'd'));
		}
	}, [lockedDate]);

	const handleDateSelect = date => {
		setSelectedDate(date);
	};

	const handleNotesChange = e => {
		const note = e.target ? e.target.value : '';
		setNotes(note);
	};

	const handleHarvestTaskChange = task => {
		setSelectedHarvestTask(task);
	};

	const handleUnit4ActivityChange = activity => {
		setSelectedUnit4Activity(activity);
	};

	const handleCancelButtonPress = () => {
		onDone();
	};

	const resetFields = () => {
		setSelectedDate(todayOrEarliestSelectableDate);
		setInputValue('');
		setNotes('');

		dispatch(EVENT_ID.RESET_INPUT);
	};

	const handleAddButtonPress = e => {
		const onSuccess = result => {
			if (result.createTimeReg.errors && result.createTimeReg.errors.length === 1 && result.createTimeReg.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD') {
				showModal({
					type: MODAL_TYPE.WARNING_DATE_LOCKED,
				});
			} else {
				createToast({
					duration: 5000,
					message: formatMessage({id: 'common.time_entry_added'}),
				});

				resetFields();
			}
		};

		if (!isNaN(inputValue) && inputValue !== null && inputValue !== '') {
			Util.CommitSchedulingModalUpdate(
				CreateTimeRegistration,
				{
					viewer: viewer,
					projectId: task.project.id,
					taskId: task.id,
					subTask: !!task.parentTaskId,
					source: 'Task-modal',
					idleTimeId: null,
					minutesRegistered: inputValue * 60,
					day: selectedDate.date(),
					month: selectedDate.month() + 1,
					year: selectedDate.year(),
					personId: viewer.actualPersonId,
					notes: notes.length ? notes : null,
					harvestTaskId: selectedHarvestTask ? selectedHarvestTask.value : null,
					unit4ActivityId: selectedUnit4Activity ? selectedUnit4Activity.value : null,
					unit4ActivityName: selectedUnit4Activity ? selectedUnit4Activity.label : null,
				},
				onSuccess
			);

			createToast({
				duration: 5000,
				message: formatMessage({id: 'common.time_entry_added'}),
			});
		} else {
			inputRef.focus();
		}
	};

	const handleAddButtonEnterPress = e => {
		if (e.key === 'Enter') handleAddButtonPress();
	};

	const handleCancelButtonEnterPress = e => {
		if (e.key === 'Enter') onDone();
	};

	const onKeyDown = e => {
		if (e.key === 'Enter') {
			handleAddButtonPress();
		}
	};

	const fetchUnit4Activities = () => {
		//If project dropdown is not empty
		if (task.project) {
			//If a project is selected, show only tasks that belong to that project
			if (task.project.id && task.project.unit4Project) {
				if (unit4Loading === true) return;
				if (unit4Activities.length === 0) {
					setUnit4Loading(true);
				}
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'unit4_activities/' + task.project.id, init)
					.then(result => result.json())
					.then(activities => {
						setUnit4Activities(activities.map(activity => ({label: activity.name, value: activity.id})));
						setUnit4Loading(false);
					});
			} else {
				setUnit4Activities([]);
				setUnit4Loading(false);
			}
		} else {
			setUnit4Activities([]);
			setUnit4Loading(false);
		}
	};

	return (
		<TimeRegContainer isTaskModal={isTaskModal}>
			<div className="popup-wrapper">
				<div className="entry-wrapper">
					<div className="title">{formatMessage({id: 'common.new_time_entry_popup_title'})}</div>
				</div>
				<div className="entry-wrapper">
					<TimeRegWSuggestionsInput
						id={id}
						initialValue={inputValue}
						onInputChange={value => {
							setInputValue(value);
						}}
						date={selectedDate}
						task={task}
						viewer={viewer}
						innerRef={div => (inputRef = div)}
						parentKeyDown={e => onKeyDown(e)}
						clearable
					/>
				</div>
				{task.project && task.project.harvestProject && viewer.harvestUser && viewer.company.harvestEnabled ? (
					<div className="entry-wrapper">
						<Dropdown
							inputClassName="harvest-dropdown"
							focusOnClick={true}
							options={harvestTaskOptions || []}
							onChange={handleHarvestTaskChange}
							value={selectedHarvestTask ? selectedHarvestTask.value : ''}
							label={formatMessage({id: 'card_modal.harvest_task'})}
							multi={false}
							clearable={true}
							customHeight={30}
							placeholder={formatMessage({id: 'card_modal.harvest_task_placeholder'})}
						/>
					</div>
				) : null}
				{task.project && task.project.unit4Project && task.project.unit4Project.activitiesEnabled && viewer.unit4User && viewer.company.unit4Enabled ? (
					<div className="entry-wrapper">
						<DropdownV2
							value={selectedUnit4Activity ? selectedUnit4Activity.value : null}
							options={unit4Activities}
							onChange={handleUnit4ActivityChange}
							label={formatMessage({id: 'integrations.unit4_activity'})}
							placeholder={formatMessage({id: 'integrations.unit4_activity_placeholder'})}
							customHeight={30}
							onClick={fetchUnit4Activities}
							integrationDropdown={true}
							integrationLoading={unit4Loading}
							integrationRetryToFetch={fetchUnit4Activities}
						/>
					</div>
				) : null}
				<div className="entry-wrapper">
					<div className="datepicker">
						<div className="input-title">{formatMessage({id: 'common.date'})}</div>
						<DatePicker
							startDate={selectedDate}
							handleDateRangeChange={handleDateSelect}
							datePickerStyle={DATE_PICKER_STYLE.STANDARD}
							isSingleDatePicker={true}
							disabled={false}
							startDateLimite={lockedDate && lockedDate.clone().add(1, 'd')}
						/>
					</div>
				</div>
				<div className="entry-wrapper">
					<div className="notes">
						<div className="input-title">{formatMessage({id: 'common.notes'})}</div>
						<textarea maxLength={viewer.company.characterLimit > -1 ? viewer.company.characterLimit : 999} rows={5} cols={40} placeholder={formatMessage({id: 'overview_time.write_your_notes'})} onChange={handleNotesChange.bind(this)} />
					</div>
				</div>
				<div className="buttons">
					<Button
						text={formatMessage({id: 'common.cancel'})}
						buttonStyle={BUTTON_STYLE.FILLED}
						colorTheme={BUTTON_COLOR.WHITE}
						onKeyDown={handleCancelButtonEnterPress}
						onClick={handleCancelButtonPress}
						focusOnMouseDown={true} // need to focus on the button when clicking down to trigger the blur effect on the input to tranform string to hours amount
						focusOnClick={true}
					/>
					<Button
						text={formatMessage({id: 'common.add'})}
						buttonStyle={BUTTON_STYLE.FILLED}
						colorTheme={BUTTON_COLOR.GREEN}
						onClick={handleAddButtonPress}
						onKeyDown={handleAddButtonEnterPress}
						focusOnMouseDown={true}
						focusOnClick={true}
						cy="task-modal-entries-button-add-button"
						isDisabled={task.project && task.project.harvestProject && viewer.company.harvestEnabled && viewer.harvestUser && !selectedHarvestTask}
					/>
				</div>
			</div>
		</TimeRegContainer>
	);
};

TimeReg.propTypes = {
	onDone: PropTypes.func,
	viewer: PropTypes.any.isRequired,
	task: PropTypes.any.isRequired,
	harvestTaskOptions: PropTypes.array,
	unit4ActivityOptions: PropTypes.array,
};

TimeReg.defaultProps = {
	onDone: () => true,
};
export default injectIntl(TimeReg);
